import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { subHours } from 'date-fns';
import { IconButton, Slide, Stack, Tab, Tabs, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import styled from 'styled-components';

import { selectedDeviceAtom, selectedDeviceSafeAtom } from '../common/MapView';
import { DEVICE_SIDEBAR_WIDTH } from '.';
import { PROJECT_SIDEBAR_WIDTH } from '@/components/projects';
import DeviceDetailStatusCards from '@/components/devices/DeviceDetailStatusCards';
import DeviceDetailOverview from './DeviceDetailOverview';
import DeviceDetailReports from './DeviceDetailReports';
import DeviceDetailSettings from './DeviceDetailSettings';
import DeviceDetailSubscription from '@/components/devices/DeviceDetailSubscription';

import type { Period } from '@/@types/device';

export const deviceDataPeriodAtom = atom<Period>({ from: subHours(new Date(), 24), to: new Date() });

const CloseButton = styled(IconButton)`
  background-color: #7F7F7F;
  color: white;
  &:hover {
    background-color: #A0A0A0;
  }
`;
function TabPanel({ index, ...props }: { index: number }) {
  const tabs = [DeviceDetailOverview, DeviceDetailReports, DeviceDetailSettings, DeviceDetailSubscription];
  const SelectedTab = tabs[index];
  return <SelectedTab {...props} />;
}

export default function DeviceDetail() {
  const { t } = useTranslation();
  const [selectedDevice, setSelectedDevice] = useAtom(selectedDeviceAtom);
  const setPeriod = useSetAtom(deviceDataPeriodAtom);
  const device = useAtomValue(selectedDeviceSafeAtom);
  const isOpen = selectedDevice ? true : false;
  const [tabIndex, setTabIndex] = useState(0);
  const [left, setLeft] = useState(0);

  const location = useLocation();
  const isDevicesMenu = location.pathname === '/devices';

  useEffect(() => {
    if (location.pathname.startsWith('/setting')) {
      setSelectedDevice(null);
    } else if (location.pathname === '/devices') {
      setLeft(DEVICE_SIDEBAR_WIDTH);
    } else {
      setLeft(0);
    }
  }, [location.pathname, setSelectedDevice]);
  useEffect(() => {
    if (location.pathname === '/projects') return;
    setPeriod({ from: subHours(new Date(), 24), to: new Date() });
  }, [setPeriod, selectedDevice, location.pathname]);

  if (!device) return <></>;

  function onEnter() {
    if (isDevicesMenu) {
      setLeft(DEVICE_SIDEBAR_WIDTH);
    } else if (location.pathname === '/projects') {
      setLeft(PROJECT_SIDEBAR_WIDTH);
    } else {
      setLeft(0);
    }
  }
  function onExited() {
    setTabIndex(0);
  }
  function onCloseClick() {
    setSelectedDevice(null);
  }

  return (
    <Slide in={isOpen} direction="right" timeout={isDevicesMenu ? 400 : 300} onEnter={onEnter} onExited={onExited}>
      <Stack
        gap={2}
        sx={{
          position: 'absolute',
          top: 0,
          left,
          width: 620,
          height: '100%',
          p: 3,
          bgcolor: '#F7F9FA',
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h5">
            {/* TODO: 그룹설정이 추가되면 활성화 */}
            {/* {device.group} - {device.name} */}
            {device.name}
          </Typography>
          <CloseButton onClick={onCloseClick}>
            <Close />
          </CloseButton>
        </Stack>

        <DeviceDetailStatusCards />

        <Tabs value={tabIndex} onChange={(_, i) => setTabIndex(i)}>
          <Tab label={t('Devices.label.overview')} />
          <Tab label={t('Common.label.reports')} />
          <Tab label={t('Devices.label.settings')} />
          <Tab label={t('Common.label.subscription')} />
        </Tabs>
        <TabPanel index={tabIndex} />
      </Stack>
    </Slide>
  );
}
